import type { Role } from "../services/types";

export interface RoleDefinition {
  value: Role;
  label: string;
  description: string;
}

export const ROLES: RoleDefinition[] = [
  { value: "super_admin", label: "Super Admin", description: "Full access including settings" },
  { value: "admin", label: "Admin", description: "Full operational access" },
  { value: "collection_agent", label: "Collection Agent", description: "Collections and member lookup" },
  { value: "accountant", label: "Accountant", description: "All financial modules, no settings" },
  { value: "viewer", label: "Viewer", description: "Read-only access across the app" },
];

export function roleLabel(role: Role): string {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return ROLES.find((r) => r.value === norm)?.label ?? role;
}

/**
 * Which nav section keys each role can see.
 * `viewer` sees everything but has all action buttons disabled.
 */
export const ROLE_NAV: Record<Role, string[]> = {
  super_admin: ["dashboard", "members", "groups", "auction", "collection", "payout", "reports", "settings"],
  admin: ["dashboard", "members", "groups", "auction", "collection", "payout", "reports", "settings"],
  collection_agent: ["dashboard", "collection", "members"],
  accountant: ["dashboard", "members", "groups", "auction", "collection", "payout", "reports"],
  viewer: ["dashboard", "members", "groups", "auction", "collection", "payout", "reports", "settings"],
};

export function canAccess(role: Role, navKey: string): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return ROLE_NAV[norm]?.includes(navKey) ?? false;
}

/** Viewer cannot perform mutating actions; everyone else can. */
export function canAct(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm !== "viewer";
}

/**
 * Financial management actions (reversing receipts, waiving/applying late fees)
 * are restricted to the organizer roles. Collection Agents may record payments
 * but may not reverse them or waive fees.
 */
export function canManageFinancials(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin" || norm === "accountant";
}

/**
 * Payout approval and reversal are the most sensitive actions in the app and
 * are restricted to the owner roles only. Accountants may prepare and record a
 * disbursement (canManageFinancials) but may not authorise or reverse one.
 */
export function canApprovePayouts(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin";
}

export function canReversePayouts(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin";
}

/**
 * Collection transaction deletion and reversal can only be performed by Super Admin and Admin.
 */
export function canReverseCollection(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin";
}

/** Super Admin and Admin may view the staff users page. */
export function canViewUsers(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin";
}

/**
 * Super Admin can delete any user except self.
 * Admin can delete ONLY collection agents and viewers.
 */
export function canDeleteUser(targetRole: Role, actorRole: Role, isSelf: boolean = false): boolean {
  if (isSelf) return false;
  const actor = (actorRole ? String(actorRole).toLowerCase() : "") as Role;
  const target = (targetRole ? String(targetRole).toLowerCase() : "") as Role;

  if (actor === "super_admin") return true;
  if (actor === "admin") {
    return target === "collection_agent" || target === "viewer";
  }
  return false;
}

/** Admin and Super Admin may delete members. */
export function canDeleteMember(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin";
}

/** Admin and Super Admin may delete groups. */
export function canDeleteGroup(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin";
}

/** Only Super Admin manages staff users (create, edit, reset password) and document numbering. */
export function canManageUsers(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin";
}

/** Only Super Admin may change document numbering sequences. */
export function canManageNumbering(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin";
}

/** Admin and Super Admin edit business settings. */
export function canEditBusinessSettings(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin";
}

/** Admin and Super Admin may view the audit log. */
export function canViewAudit(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin";
}

/** Super Admin and Admin may view and manage IP/device access approvals. */
export function canManageDevices(role: Role): boolean {
  const norm = (role ? String(role).toLowerCase() : "") as Role;
  return norm === "super_admin" || norm === "admin";
}